import { Plus, Search } from "lucide-react";
import React, { useMemo } from "react";
import { Updater, useImmer } from "use-immer";

import {
  IgButton,
  IgLink,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { CustomEventDto, CustomEventFeType } from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { ArchivedEventList } from "@src/features/customEvents/components/ArchivedEventList";
import { CreateNewCustomEventBigButton } from "@src/features/customEvents/components/CreateNewCustomEventBigButton";
import { CustomEventList } from "@src/features/customEvents/components/CustomEventList";
import { theme } from "@src/theme";
import { customEventsLink } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface SelectExistingCustomMetricsProps {
  isCreating: boolean;
  setIsCreating: Updater<boolean>;
  customEventsData: CustomEventDto[];
  keyMetricRender?: boolean;
  selectedCustomEventIds: string[];
  setSelectedCustomEventIds: Updater<string[]>;
  setSubpageSelected: Updater<CustomEventFeType | null>;
  setDrawerPage: Updater<string>;
  handleShowWarningDialog?: (eventKey: CustomEventFeType) => void;
}

export const SelectExistingCustomMetrics = ({
  isCreating,
  customEventsData,
  setIsCreating,
  keyMetricRender,
  selectedCustomEventIds,
  setSelectedCustomEventIds,
  setSubpageSelected,
  setDrawerPage,
}: SelectExistingCustomMetricsProps) => {
  const [search, setSearch] = useImmer<string>("");

  const filteredCustomEvents = useMemo(() => {
    if (!search.trim()) {
      return customEventsData;
    }
    return customEventsData.filter((event) =>
      event.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, customEventsData]);

  if (isCreating) {
    return null;
  }
  return (
    <Stack mt={5} direction={"column"} spacing={4}>
      {keyMetricRender ? null : (
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          marginTop={"20px"}
        >
          <Grid>
            <IgTypography fontWeight={600} variant={"h2"}>
              Select Custom Metrics
            </IgTypography>
          </Grid>
          {customEventsData.length ? (
            <Grid>
              <IgTextField
                placeholder={"Search for metric"}
                slotProps={{
                  input: {
                    endAdornment: <Search size={16} />,
                  },
                }}
                sx={{ width: "300px" }}
                value={search ?? ""}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Grid>
          ) : null}
        </Grid>
      )}
      {customEventsData.length ? (
        <Stack direction={"column"} spacing={4}>
          <IgTypography color={theme.palette.text.secondary}>
            Select existing custom metrics or define new ones.{" "}
            <IgLink
              variant={"body1"}
              onClick={() => {
                openNewTab(customEventsLink, "Custom Events - Overview");
              }}
            >
              Learn more.
            </IgLink>
          </IgTypography>
          <IgButton
            variant={"outlined"}
            sx={{
              width: "100%",
              // marginTop: "30px",
            }}
            onClick={() => {
              setIsCreating(true);
              setDrawerPage("subpage");
            }}
            startIcon={<Plus size={18} />}
          >
            Create New Custom Metric
          </IgButton>
          <CustomEventList
            customEvents={filteredCustomEvents}
            selectedCustomEventIds={selectedCustomEventIds}
            setSelectedCustomEventIds={setSelectedCustomEventIds}
            setIsCreating={setIsCreating}
            setSubpageSelected={setSubpageSelected}
            setDrawerPage={setDrawerPage}
          />
          <ArchivedEventList keyMetricRender={keyMetricRender} />
        </Stack>
      ) : (
        <CreateNewCustomEventBigButton
          onClick={() => {
            setIsCreating(true);
            setDrawerPage("subpage");
          }}
        />
      )}
    </Stack>
  );
};
